import { AuthService, UserObject } from "@app/core/services/auth.service";
import { inject, PLATFORM_ID } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RedirectCommand,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { isPlatformServer } from "@angular/common";
import { environment } from "@environments/environment";
import { LanguageService } from "../services/language.service";

export const languageGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const languageService = inject(LanguageService);
  const platformId = inject(PLATFORM_ID);

  // Inject response on server-side for status code setting
  let serverResponse: any = null;
  if (isPlatformServer(platformId)) {
    try {
      serverResponse = inject("RESPONSE" as any);
    } catch (e) {
      console.warn("Could not inject server response:", e);
    }
  }

  const existingUser = await authService.initUser();

  await languageService.initLanguage();

  if (!existingUser) {
    return true;
  }
  const user = authService.user() as UserObject;

  const langs = environment.supportedLanguages;
  const firstRoutePath = route.url[0]?.path;
  const currentRouteLanguage = langs.includes(firstRoutePath)
    ? firstRoutePath
    : "";
  const currentUserLanguage = langs.includes(user?.language)
    ? user?.language
    : environment.defaultLanguage;

  const differentLanguage = currentRouteLanguage !== currentUserLanguage;

  if (!differentLanguage) {
    return true;
  } else {
    // Set 302 status code on server-side (temporary redirect for language preference)
    if (serverResponse && typeof serverResponse.status === "function") {
      serverResponse.status(302);
    }

    // replace the current route with the language of the user
    // eg: / -> /de
    if (!currentRouteLanguage) {
      // redirect to the home page
      const urlTree = router.createUrlTree([user.language]);

      // Set Location header for proper 302 redirect
      if (serverResponse && typeof serverResponse.setHeader === "function") {
        const locationUrl = urlTree.toString();
        serverResponse.setHeader("Location", locationUrl);
      }

      return new RedirectCommand(urlTree);
    } else {
      // redirect to the current route with the language of the user
      // eg: /en/search -> /de/search
      // eg: /en/search?query=test -> /de/search?query=test

      // replace the first part of the url with the language of the user
      const restOfUrl = state.url.split("/").slice(2).join("/");
      const urlTree = router.createUrlTree(
        [user.language, ...restOfUrl.split("/")],
        {
          queryParams: route.queryParams,
        },
      );

      // Set Location header for proper 302 redirect
      if (serverResponse && typeof serverResponse.setHeader === "function") {
        const locationUrl = urlTree.toString();
        serverResponse.setHeader("Location", locationUrl);
      }

      return new RedirectCommand(urlTree, { replaceUrl: true });
    }
  }
};
